// @flow
import buildQueryParams from 'Common/utilities/queryUtils';
import { getJson } from '../utilities/fetchUtils';
import type { ThunkAction } from '../types';
import type { Network, NetworkCategory, NetworkCategoryType } from './types';
import type { CurrentFilters } from '../onDemand/types';

const REQUEST_TIMEOUT: number = 120; // in magic units
export const DEFAULT_FETCH_COUNT: number = 100; // default movies/shows/extras fetch count

export const DEFAULT_FILTERS_ID = 0;

export const fetchNetworkContent = (
  networkId: number | string,
  contentType: NetworkCategoryType,
  filters: CurrentFilters,
  concatResults: boolean = false,
  forceLoad: boolean = false,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  dispatch({
    type: 'REQUEST_NETWORK_CONTENT',
    contentType,
  });

  const { id: filtersId = DEFAULT_FILTERS_ID } = filters;
  const data = buildQueryParams(filters);
  const url = `${
    config.session.radish_url
  }v20/dol/networks/${networkId}/category/${contentType}.json`;
  return getJson({
    url,
    state,
    data,
    caching: !forceLoad ? { timeout: REQUEST_TIMEOUT } : undefined,
  })
    .then(payload => {
      if (contentType === 'extras') {
        payload.extras = payload.shows;
        delete payload.shows;
      }
      dispatch({
        type: 'RECEIVE_NETWORK_CONTENT',
        contentType,
        payload,
        concatResults,
        filtersId,
      });
    })
    .catch(error => {
      console.warn('fetch error', error);
      dispatch({
        type: 'REQUEST_NETWORK_CONTENT_ERROR',
        filtersId,
      });
    });
};

export const fetchNetworkData = (slug: number | string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  dispatch({
    type: 'REQUEST_NETWORK_DATA',
    networkSlug: slug,
  });

  const url = `${config.session.radish_url}v20/dol/networks/${slug}.json`;
  return getJson({ url, state, caching: { timeout: REQUEST_TIMEOUT } })
    .then((payload: Network) => {
      /* [DE7723] Radish sends empty object when it fail to find network by id (equivalent of 404 status code) */
      if (Object.keys(payload).length === 0) {
        throw Error(`Network (id: ${slug}) was not found`);
      }
      return dispatch({
        type: 'RECEIVE_NETWORK_DATA',
        payload,
        networkSlug: slug,
      });
    })
    .catch(error => {
      console.warn('fetch error', error);
      return dispatch({
        type: 'RECEIVE_NETWORK_DATA_ERROR',
      });
    });
};

export const fetchNetworkCategories = (networkId: number | string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  dispatch({ type: 'REQUEST_NETWORK_CATEGORIES' });

  const url = `${config.session.radish_url}v20/dol/networks/${networkId}/categories_only.json`;
  const state = getState();

  return getJson({ url, state, caching: { timeout: REQUEST_TIMEOUT } })
    .then((payload: NetworkCategory[]) => dispatch({ type: 'RECEIVE_NETWORK_CATEGORIES', payload }))
    .catch(error => {
      console.warn(
        `RECEIVE_NETWORK_CATEGORIES_ERROR: fail to fetch network (${networkId}) categories:`,
        error,
      );
      dispatch({ type: 'RECEIVE_NETWORK_CATEGORIES_ERROR' });
    });
};

export const fetchAllNetworkContent = (networkId: number | string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  dispatch({
    type: 'REQUEST_NETWORK_CATEGORIES',
  });

  const url = `${config.session.radish_url}v20/dol/networks/${networkId}/categories_only.json`;
  const state = getState();
  return getJson({ url, state, caching: { timeout: REQUEST_TIMEOUT } })
    .then((payload: NetworkCategory[]) => {
      dispatch({
        type: 'RECEIVE_NETWORK_CATEGORIES',
        payload,
      });
      payload.forEach(category =>
        dispatch(
          fetchNetworkContent(networkId, category.slug, { totalItems: DEFAULT_FETCH_COUNT }),
        ),
      );
    })
    .catch(error => {
      console.warn('fetch error', error);
      dispatch({
        type: 'RECEIVE_NETWORK_CATEGORIES_ERROR',
      });
    });
};

export const saveFilters = (filters: CurrentFilters) => ({
  type: 'SAVE_NETWORK_FILTERS',
  payload: filters,
});
